import output from "_src/engine/output";
import events from "_src/engine/events";
import intersectEvaluator from "_src/engine/intersect_evaluator";
import { easingType, cameraType, geometry } from "_src/engine/constants";

import {vec3, vec4, quat, mat4} from "_src/libs/gl-matrix";

import { calcOrthoCameraProjection, getPositionByMinmax, calcOrthoCameraFrustum } from "_src/utils";
import utils from "_src/client/controls/utils";
import CameraControl from "_src/client/controls/camera_control";

const ZOOM_TO_CURSOR = true;

const _vec3Tmp = vec3.create();
const _vec3Tmp2 = vec3.create();
const _vec3Tmp3 = vec3.create();

const _quatTmp = quat.create();

export default class TargetControl extends CameraControl {

  constructor(object) {
    super(object);

    this.target = vec3.create();
    this.dist = 10;
    this.minDist = 0.5;

    this.horAngle = 0;
    this.vertAngle = 0;

    this.direction = vec3.create();
    this.upVec = geometry.AXIS_Z;

    this.isUpsideDown = false;
    this.upsideDownSign = 1;

    this.prevTouchX = -1;
    this.prevTouchY = -1;
    this.prevTouchesDist = -1;
    this.prevTouchesCenter = new Float32Array([-1, -1]);

    this.updateUpsideDown();
    const trans = this._getObjectTrans();
    this.setPosition(trans);
  }

  setPosition(trans) {

    if (!this.enabled) {
      return;
    }

    this.updateUpsideDown();
    this._updateDirection(trans);

    const [horAngle, vertAngle] = utils.anglesFromViewOriented(this.direction);

    const distance = vec3.distance(this.target, trans);

    this.setAngles(horAngle, vertAngle);

    this.setDistance(distance);

  }

  updatePosition() {
    const object = this.object;

    if (!this.enabled) {
      return;
    }

    utils.perspectiveTransFromTargetAngles(this.horAngle, this.vertAngle, this.target, this.dist, object.trans);

    object.copyTranslation(object.trans);

    events.emit("camera_move", object);

  }

  updateRotation() {
    const object = this.object;

    if (!this.enabled) {
      return;
    }

    const trans = this._getObjectTrans();
    this._updateDirection(trans);

    const objX = this._getObjectRight(_vec3Tmp, this.horAngle);
    const objY = vec3.cross(_vec3Tmp2, objX, this.direction);

    quat.setAxes(object.quat, this.direction, objX, objY);
    quat.conjugate(object.quat, object.quat);
    object.copyRotation(object.quat);

    events.emit("camera_move", object);

  }

  setDistance(dist) {
    this.dist = dist;
    this.updatePosition();
  }

  setCriticalDistance(dist) {
    this.minDist = dist;
  }

  zoom(delta, cameraX, cameraY) {

    if (!this.enabled) {
      return;
    }

    if (ZOOM_TO_CURSOR && this.object.isCamera) {

      this._zoomCameraToCursor(delta, cameraX, cameraY);

    } else {

      this.dist *= 1.0 + delta;

      if (this.dist < this.minDist) {
        this.dist = this.minDist;
      }

    }

    this.object.top *= 1.0 + 0.5 * delta;
    this.object.bottom *= 1.0 + 0.5 * delta;

    this.updatePosition();

    events.emit("control_zoom", this.object);

  }

  setAngles(hor, vert) {
    this.horAngle  = hor;
    this.vertAngle = vert;
    this.updatePosition();
  }

  setTarget() {}
  startRotation() {}
  rotate() {}
  setTargetVisibility() {}

  endRotation() {

    if (!this.enabled) {
      return;
    }

    const trans = this._getObjectTrans();
    this._updateDirection(trans)
    this.updateUpsideDown();
  }

  lookAt(x, y, z) {

    vec3.set(this.target, x, y, z);

    const trans = this._getObjectTrans();
    this.dist = vec3.distance(this.target, trans);
    this._updateDirection(trans);

    this.updateUpsideDown();

  }

  updateUpsideDown() {
    this.isUpsideDown = utils.checkIsUpsideDown(this.object.quat, this.upVec);
    this.upsideDownSign = this.isUpsideDown ? -1 : 1;
  }

  setDefaultPosition(sceneBBox) {

    const savedEnableState = this.enabled;
    this.enabled = true;

    const coordZ = utils.calculateBBoxOptimalDistance(sceneBBox, this.object);

    this.lookAt(0.5 * (sceneBBox[0] + sceneBBox[3]),
      0.5 * (sceneBBox[1] + sceneBBox[4]),
      0.5 * (sceneBBox[2] + sceneBBox[5]));
    this.setDistance(coordZ);

    const {width, height} = calcOrthoCameraProjection(this.object, this.target);
    this.object.top = height;
    this.object.bottom = -height;
    this.object.right = width;
    this.object.left = -width;

    this.updateRotation();

    this.enabled = savedEnableState;

  }

  setProjectionType(type, gridCamera, onStart, onUpdate, onFinish, time) {

    const object = this.object;

    if (object.type == type) {
      return;
    }

    const startProjMat = mat4.create();
    const finalMatrix = mat4.create();

    const startGridMat = mat4.create();
    const finalGridMatrix = mat4.create();

    const {width, height} = calcOrthoCameraProjection(object, this.target);

    switch (type) {
    case cameraType.PERSPECTIVE:
      this._animation.type = easingType.IN_EXPO;

      object.near = Math.max(0.1, object.near);

      object.calculatePerspectiveProjMat(finalMatrix);
      object.calculateOrthoProjMat(startProjMat);

      gridCamera.calculatePerspectiveProjMat(finalGridMatrix);
      gridCamera.calculateOrthoProjMat(startGridMat);
      break;
    case cameraType.ORTHO:
      this._animation.type = easingType.OUT_EXPO;

      object.top = height;
      object.bottom = -height;
      object.right = width;
      object.left = -width;

      gridCamera.top = height;
      gridCamera.bottom = -height;
      gridCamera.right = width;
      gridCamera.left = -width;

      object.calculateOrthoProjMat(finalMatrix);
      object.calculatePerspectiveProjMat(startProjMat);

      gridCamera.calculateOrthoProjMat(finalGridMatrix);
      gridCamera.calculatePerspectiveProjMat(startGridMat);
      break;
    }

    const workspaceDelta = mat4.subtract(mat4.create(), finalMatrix, startProjMat);
    const gridDelta = mat4.subtract(mat4.create(), finalGridMatrix, startGridMat);
    const tmpMat4 = mat4.create();

    this._animation.setOnUpdate((value) => {

      const current = mat4.multiplyScalarAndAdd(tmpMat4, startProjMat, workspaceDelta, value);
      object.setProjectionMatrix(current);

      const gridCurrent = mat4.multiplyScalarAndAdd(tmpMat4, startGridMat, gridDelta, value);
      gridCamera.setProjectionMatrix(gridCurrent);

      events.emit("update_grid");
      onUpdate();

    });

    this._animation.setOnFinish(() => {

      object.setType(type);
      // NOTE: it needs to recalculate near-far here and force querry tests
      events.emit("camera_move", object);
      onFinish();

    });

    onStart();

    this._animation.start(0, 1, time);

  }

  moveToMinmax(minmax, rect, animation, onStart, onUpdate, onFinish, time = 0.2, target = null) {

    const object = this.object;

    if (!this.enabled) {
      return;
    }

    const startTarget = vec3.clone(this.target);
    const center = vec3.fromValues(0.5 * (minmax[0] + minmax[3]),
                                        0.5 * (minmax[1] + minmax[4]),
                                        0.5 * (minmax[2] + minmax[5]));
    const startPosition = vec3.copy(vec3.create(), object.trans);
    const finalPosition = getPositionByMinmax(object.quat, object.fov, object.aspect, minmax, rect, center, vec3.create());

    const invView = vec3.fromValues(0, 0, 1);
    vec3.transformQuat(invView, invView, object.quat);
    const targetPlane = vec4.fromValues(invView[0], invView[1], invView[2], -vec3.dot(invView, center));
    const targetPlainPoint = vec3.create();
    intersectEvaluator.rayPlaneIntersection(targetPlane, finalPosition, invView, targetPlainPoint);
    const finalTarget = target ? target : targetPlainPoint;

    const startHeight = object.top;
    const {height, width} = calcOrthoCameraFrustum(object.quat, minmax, rect);
    const finalHeight = height * object.aspect > width ? height : width / object.aspect;

    animation.setOnUpdate((value) => {

      vec3.lerp(this.target, startTarget, finalTarget, value);
      vec3.lerp(object.trans, startPosition, finalPosition, value);

      const height = startHeight + value * (finalHeight - startHeight);
      const width = height * object.aspect;

      object.top = height;
      object.bottom = -height;
      object.right = width;
      object.left = -width;

      this.setPosition(object.trans);

      onUpdate(value);

    });

    animation.setOnFinish(() => {

      vec3.copy(this.target, finalTarget);
      this.setPosition(finalPosition);

      object.top = finalHeight;
      object.bottom = -finalHeight;
      object.right = finalHeight * object.aspect;
      object.left = -finalHeight * object.aspect;

      onFinish();

    });

    animation.type = easingType.IN_OUT_SINE;

    animation.start(0, 1, time);

  }

  _updateDirection(trans) {
    vec3.sub(this.direction, this.target, trans);
    vec3.normalize(this.direction, this.direction);
  }

  _zoomCameraToCursor(delta, cameraX, cameraY) {

    const object = this.object;
    const rayToCursor = _vec3Tmp;

    const dist = vec3.distance(object.trans, this.target);
    const step = 0.5 * dist * (-delta);

    if (object.isPerspective()) {
      object._calculateRayPersp(cameraX, cameraY, rayToCursor);
      vec3.scale(rayToCursor, rayToCursor, step);
    } else if (object.isOrtho()) {
      const dw = 0.5 * cameraX * object.right * (-delta);
      const dh = 0.5 * cameraY * object.top * (-delta);

      vec3.set(rayToCursor, dw, dh, -step);
      vec3.transformQuat(rayToCursor, rayToCursor, object.quat);
    } else {
      output.error("Coudn't zoom to cursor: incorrect camera type.");
      return -1;
    }

    const newPosition = vec3.add(_vec3Tmp2, object.trans, rayToCursor);
    object.copyTranslation(newPosition);

    this._updateDirection(object.trans);

    const [horAngle, vertAngle] = utils.anglesFromView(this.direction);
    this.horAngle = horAngle;
    this.vertAngle = vertAngle;

    this.dist = vec3.distance(object.trans, this.target);

  }

  _getObjectRight(dst, horAngle) {
    const quatHor = quat.setAxisAngle(_quatTmp, this.upVec, horAngle);
    vec3.transformQuat(dst, geometry.AXIS_MX, quatHor);
    const sign = this.isUpsideDown ? -1 : 1;
    vec3.scale(dst, dst, sign);
    return utils.toReference(dst);
  }

  _getObjectTrans() {
    return vec3.copy(_vec3Tmp3, this.object.trans);
  }

}
